/**
 * @fileoverview Entity Picker cart model
 */
// @ts-nocheck
goog.declareModuleId('yext.ui.components.entitypicker.cart.CartModel');

import {ItemTypes} from '/ui/components/entitypicker/container/itemtypes';
import {DataModel, EntityTypeModel} from '/ui/components/entitypicker/datasource/datamodel';

/**
 * An immutable model of the entity picker cart
 */
/** @suppress {checkTypes} */
export class CartModel {
  /**
   * @param {boolean=} usingEntities
   * @param {?=} cart
   */
  constructor(usingEntities = true, cart = null) {
    const prevCart = cart || {
      total: 0,
      allSelected: [],
      entityTypes: [],
      entities: [],
      folders: [],
      labels: [],
    };
    this.total = prevCart.total;
    this.allSelected = prevCart.allSelected;
    this.entityTypes = Object.freeze([...prevCart.entityTypes]);
    this.entities = Object.freeze([...prevCart.entities]);
    this.folders = Object.freeze([...prevCart.folders]);
    this.labels = Object.freeze([...prevCart.labels]);
    this.usingEntities = usingEntities;
    Object.freeze(this);
  }

  /**
   *
   * @param {*} entity
   * @param {*=} entityType
   * @returns {?}
   */
  withEntity(entity, entityType) {
    const newItem = withEntityTypeInfo(entity, entityType);
    return this.updatedCart({
      entities: this.entities.concat(newItem),
    });
  }

  withEntityType(entityType) {
    if (this.usingEntities) {
      const newItem = withoutEntities(entityType);
      return this.updatedCart({
        entityTypes: this.entityTypes.concat(newItem),
        entities: this.entities.filter(e => e.typeId !== entityType.id),
      });
    } else {
      const newItem = withoutEntitiesOrTotal(entityType);
      return this.updatedCart({
        entityTypes: this.entityTypes.concat(newItem),
      });
    }
  }

  withFolder(folder, descendantIds) {
    return this.updatedCart({
      folders: this.folders
        .filter(f => !descendantIds.includes(f.id))
        .concat(folder),
    });
  }

  withLabel(label) {
    return this.updatedCart({
      labels: this.labels.concat(label),
    });
  }

  withoutEntity(entity) {
    return this.updatedCart({
      entities: withoutItemById(this.entities, entity),
    });
  }

  withoutEntityType(entityType) {
    return this.updatedCart({
      entityTypes: withoutItemById(this.entityTypes, entityType),
    });
  }

  withoutFolder(folder) {
    return this.updatedCart({
      folders: withoutItemById(this.folders, folder),
    });
  }

  withoutLabel(label) {
    return this.updatedCart({
      labels: withoutItemById(this.labels, label),
    });
  }

  allEntitiesSelected() {
    return this.allSelected.includes(ItemTypes.ENTITIES);
  }

  withAll(itemType) {
    if (itemType === ItemTypes.ENTITIES) {
      return this.updatedCart({
        entityTypes: [],
        entities: [],
        folders: [],
        labels: [],
        allSelected: [itemType],
      });
    } else {
      return this.updatedCart({
        folders: itemType === ItemTypes.FOLDERS ? [] : this.folders,
        labels: itemType === ItemTypes.LABELS ? [] : this.labels,
        allSelected: [itemType, ...this.allSelected],
      });
    }
  }

  withoutAll(itemType) {
    return this.updatedCart({
      allSelected: withoutItem(this.allSelected, itemType),
    });
  }

  /**
   * @param {*} subsetSelection filter prop containing a subset of entities in the current CartModel
   * @return {!CartModel}
   */
  subsetFromSelection(subsetSelection) {
    const allSelected = allSelectedFromSelection(subsetSelection);
    if (!Array.isArray(allSelected)) {
      return new CartModel().withAll(allSelected);
    } else if (allSelected.includes(ItemTypes.ENTITIES)) {
      return new CartModel().withAll(ItemTypes.ENTITIES);
    }
    return this.updatedCart({
      entityTypes: this.entityTypes.filter(selectionFieldIncludesItemPredicate(subsetSelection.entityTypes)),
      entities: this.entities.filter(selectionFieldIncludesItemPredicate(subsetSelection.entities)),
      folders: this.folders.filter(selectionFieldIncludesItemPredicate(subsetSelection.folders)),
      labels: this.labels.filter(selectionFieldIncludesItemPredicate(subsetSelection.labels)),
      allSelected,
    });
  }

  updatedCart(updates) {
    // if we are adding to the cart after we've already selected all entities, then
    // just apply new changes to a new, empty cart because  we can't select both
    // All Entities and other items
    const newCart = this.allEntitiesSelected()
      ? Object.assign({}, new CartModel(this.usingEntities), updates)
      : Object.assign({}, this, updates);
    newCart.total = cartHasMultipleTypes(newCart) ? calculateCartItemTotal(newCart)
        : calculateCartTotal(newCart, this.usingEntities);
    return new CartModel(this.usingEntities, newCart);
  }

  /**
   * @return {?} the first item found in the cart
   */
  getFirstItem() {
    for (const arr of [this.entityTypes, this.entities, this.folders, this.labels]) {
      if (arr.length) {
        return arr[0];
      }
    }
    return null;
  }

  /**
   * @return {boolean} Whether or not there are multiple item types present in
   * the cart, i.e. both folders and labels
   */
  hasMultipleTypes() {
    return cartHasMultipleTypes(this);
  }

  typesInCart() {
    const types = [];
    if (this.usingEntities && (this.entityTypes.length || this.entities.length
        || this.allSelected.includes(ItemTypes.ENTITIES))) {
      types.push(itemTypeToString(ItemTypes.ENTITIES));
    }
    if (this.folders.length || this.allSelected.includes(ItemTypes.FOLDERS)) {
      types.push(itemTypeToString(ItemTypes.FOLDERS));
    }
    if (this.labels.length || this.allSelected.includes(ItemTypes.LABELS)) {
      types.push(itemTypeToString(ItemTypes.LABELS));
    }
    if (this.entityTypes.length || this.allSelected.includes(ItemTypes.ENTITYTYPES)) {
      types.push(itemTypeToString(ItemTypes.ENTITYTYPES));
    }
    return types;
  }

  isEmpty() {
    return this.total === 0 && this.allSelected.length == 0;
  }

  /**
   * Given a master list of items, creates a cart with the items from the url object
   * if allSelected is provided as a single item type rather than an array of types,
   * cart will be created with a single type.
   * @param {!DataModel} items - Model containing the data necessary for the items in urlObject
   * @param {!Object} urlObject - Saved filter state
   * @param {boolean=} usingEntities whether the picker
   * @return {!CartModel}
   */
  static fromUrlObject(
    {
      entities = {types: []},
      folders = {items: []},
      labels = {items: []},
    },
    urlObject,
    usingEntities = true,
  ) {
    if (!urlObject) {
      return new CartModel();
    }

    const allSelected = allSelectedFromSelection(urlObject);
    if (!Array.isArray(allSelected)) {
      return new CartModel().withAll(allSelected);
    } else if (allSelected.includes(ItemTypes.ENTITIES)) {
      return new CartModel().withAll(ItemTypes.ENTITIES);
    }

    const entityTypes = removeEntitiesFromTypes(entities.types);
    const typedEntities = addTypeInfoToEntities(
      entities.types.map(t => t.entities).flat(),
      entityTypes,
    );

    // TODO(amullings): Make resilient to passing in non-arrays
    const matchId = ids => item => ids.includes(item.id);
    const model = {
      entityTypes: entityTypes.filter(matchId(urlObject.entityTypes || [])),
      entities: typedEntities.filter(matchId(urlObject.entities || [])),
      folders: allSelected.includes(ItemTypes.FOLDERS) ? []
          : folders.items.filter(matchId(urlObject.folders || [])),
      labels: allSelected.includes(ItemTypes.LABELS) ? []
          : labels.items.filter(matchId(urlObject.labels || [])),
      allSelected: allSelected,
    };
    model.total = cartHasMultipleTypes(model) ? calculateCartItemTotal(model)
        : calculateCartTotal(model, usingEntities);
    return new CartModel(usingEntities, model);
  }
}

/**
 * @param {*} cart
 * @param {*} usingEntities
 * @returns {number}
 */
function calculateCartTotal(cart, usingEntities) {
  return cart.entities.length +
    cart.folders.length +
    cart.labels.length +
    (usingEntities
      ? cart.entityTypes.reduce((subtotal, type) => subtotal + type.total, 0)
      : cart.entityTypes.length);
}
/**
 * @param {*} cart
 * @returns {number}
 */
function calculateCartItemTotal(cart) {
  return cart.entities.length +
    cart.folders.length +
    cart.labels.length +
    cart.entityTypes.length;
}

/**
 * @param {!CartModel} cart the CartModel object whose type to determine
 * @return {boolean} Whether or not there are multiple item types present in
 * the cart, i.e. both folders and labels
 */
function cartHasMultipleTypes(cart) {
  const numTypes =
      !!(cart.entityTypes.length || cart.entities.length) +
      !!cart.folders.length +
      !!cart.labels.length +
      cart.allSelected.length;
  return numTypes > 1;
}

/**
 * @param {*} types
 * @returns {*}
 */
function removeEntitiesFromTypes(types) {
  return types.map(withoutEntities);
}

/**
 * @param {*} entities
 * @param {*} types
 * @returns {*}
 */
function addTypeInfoToEntities(entities, types) {
  const entityTypesById = types.reduce(
    (map, type) => {
      map[type.id] = type;
      return map;
    },
    {});
  return entities.map(e => withEntityTypeInfo(e, entityTypesById[e.typeId]));
}

/**
 * @typedef {{
 *   total: number,
 *   id: number,
 *   label: string,
 *   apiName: (string|undefined),
 * }}
 */
export let EntityTypeWithoutEntities;

/**
 * @param {!EntityTypeModel} entityType the entityType from which to remove the entities property
 * @return {!EntityTypeWithoutEntities} a new entityType object without the entities property
 */
function withoutEntities(entityType) {
  return {
    total: entityType.total,
    id: entityType.id,
    label: entityType.label,
    apiName: entityType.apiName,
  };
}

/**
 * @typedef {{
 *   id: number,
 *   label: string,
 *   apiName: (string|undefined),
 * }}
 */
export let EntityTypeWithoutEntitiesOrTotal;

/**
 * @param {!EntityTypeModel} entityType the entityType from which to remove the entities and total properties
 * @return {!EntityTypeWithoutEntitiesOrTotal} a new entityType object without the entities and total properties
 */
function withoutEntitiesOrTotal(entityType) {
  return {
    id: entityType.id,
    label: entityType.label,
    apiName: entityType.apiName,
  };
}

/**
 * @param {!Object} entity the entity that the type is being added to
 * @param {!Object} entityType the entityType to add the the entity
 * @return {!Object} a new entity object with entity type info, to aid with things like
 * rendering entity icons.
 */
function withEntityTypeInfo(entity, entityType) {
  return Object.assign(
    {},
    entity,
    {type: entityType});
}

/**
 * @param {*} arr
 * @param {*} item
 * @returns {*}
 */
function withoutItemById(arr, item) {
  return arr.filter(i => i.id !== item.id);
}

/**
 * @param {!Array} arr the list of items
 * @param {!Object} item the item to remove
 * @return {!Array} an array without the item
 */
function withoutItem(arr, item) {
  return arr.filter(i => i !== item);
}

/**
 * @param {*} str the String version of the itemType
 * @return {string|null} the itemType symbol represented by the string param or null if
 * the string doesn't represent an itemType
 */
function itemTypeFromString(str) {
  switch (str) {
    case 'ENTITIES':
      return ItemTypes.ENTITIES;
    case 'FOLDERS':
      return ItemTypes.FOLDERS;
    case 'LABELS':
      return ItemTypes.LABELS;
    case 'ENTITYTYPES':
      return ItemTypes.ENTITYTYPES;
    default:
      return null;
  }
}

/**
 * @param {*} type something that could be an itemType symbol
 * @return {?} the validated symbol or null if param is not an itemType
 */
function validateItemType(type) {
  return Object.values(ItemTypes).includes(type) ? type : null;
}

/**
 * @param {*} type something that could be an itemType symbol
 * @return {?string} the string version of the itemType or null if param is not an itemType
 */
function itemTypeToString(type) {
  switch (type) {
    case ItemTypes.ENTITIES:
      return 'ENTITIES';
    case ItemTypes.FOLDERS:
      return 'FOLDERS';
    case ItemTypes.LABELS:
      return 'LABELS';
    case ItemTypes.ENTITYTYPES:
      return 'ENTITYTYPES';
    default:
      return null;
  }
}

/**
 * @param {?Array} selectionField an object value from the entitypickercontroller's selection prop
 * @return {function(*): boolean} predicate which takes an item and returns whether the selection field includes the item
 */
function selectionFieldIncludesItemPredicate(selectionField) {
  return item => !!selectionField && selectionField.includes(item.id);
}

/**
 * @param {*} selection entitypickercontroller's selection prop
 * @returns {*} allSelected value for CartModel
 */
function allSelectedFromSelection(selection) {
  let allSelected = [];
  if (selection.allSelected) {
    if (!Array.isArray(selection.allSelected)) {
      allSelected = validateItemType(selection.allSelected)
          || itemTypeFromString(selection.allSelected);
    } else {
      allSelected = selection.allSelected.map(
        type => validateItemType(type) || itemTypeFromString(type),
      );
    }
  }
  return allSelected;
}
